import { ab } from './interface'
import {
  Experiment,
  TrialByPlan15535Variant,
  FuzzySearch15538Variant,
  IosExternalLinkPayment16218Variant,
  Streaks16464Variant,
  OnboardingRemoveReadingList17546Variant,
  IntegratedCheckoutExperience17351Variant,
  PersistentPartnershipOffer17477Variant,
  NullExperiment17503Variant,
} from './types'

import { runTextUpdates } from '@/services/ab/13448_ab_experiment'
import Cookies from 'js-cookie'
import { platform } from '@/services/platform'
import { platformData } from '@/services/platform.capacitor'
import { getIosStorefrontLocation } from '@/services/capacitor.storefrontLocationPlugin'
import { pushABExperimentsToDataLayer } from '@/analytics/gtm'
import { storage } from '@/helpers/storage'
import { VARIANT_TO_SET_17477_KEY } from './experiments.public'

/**
 * Initialize CloudFront experiments.
 *
 * Check A/B testing cookies set by CloudFront, send backend
 * requests and update frontend a/b data storage.
 * Pushes AB experiments to GTM data layer when done.
 */
export async function initAB(): Promise<void> {
  await newCopy13448Experiment(Experiment.new_copy_pdf_v2_13448, '/pdf')
  await aiGuideFlow13747()
  savePersistentPartnershipOffer17477Variant()
  await homepageKitchenSinkRedesign17590()
  pushABExperimentsToDataLayer()
}

/**
 * Feature flag to enable/disable Google Billing. Disabled by default.
 *
 * This is not a real A/B experiment, i.e. the experiment is not activated on
 * the backend.
 */
export function googleBillingEnabled(): boolean {
  return ab.experimentVariant(Experiment.google_billing_7517) === 'yes'
}

/**
 * Apply the text changes on the public pages
 *
 * @param {Experiment} experiment - The A/B experiment containing the text changes.
 * @param {string} allowedUrlExpression - A regular expression to match allowed URLs,
 * on which the text changes should be applied.
 */
export async function newCopy13448Experiment(
  experiment: Experiment,
  allowedUrlExpression: string,
): Promise<void> {
  const pathname = window.location.pathname
  const isTranslatedPage = pathname.includes(`/es${allowedUrlExpression}/`)

  if (!pathname.includes(allowedUrlExpression) || isTranslatedPage) {
    // Only start experiment on non-translated pdf pages
    return
  }

  let result = ab.experimentVariant(experiment)

  if (result === null) {
    result = await ab.start(experiment)
  }

  if (result === 'yes') {
    runTextUpdates(experiment)
  }
}

/**
 * Get the variant of the ai_label_13537 experiment.
 * Returns boolean:
 *   true - Show the ai label component
 *   false - Don't show the ai label component
 */
export function aiLabel13537ExperimentVariant(): boolean {
  return ab.experimentVariant(Experiment.ai_label_13537) === 'yes'
}

/**
 * Decide wheter or not to render the ai content label component in
 * the book preview and summary pages.
 *   true - Show the ai label component
 *   false - Don't show the ai label component
 */
export async function aiLabel13537Experiment(): Promise<boolean> {
  let result = ab.experimentVariant(Experiment.ai_label_13537)

  if (result === null) {
    result = await ab.start(Experiment.ai_label_13537)
  }

  return result === 'yes'
}

/*
 * Read experiment cookies set by CloudFront and transfer the experiment assignment to the local storage
 */
export async function aiGuideFlow13747(): Promise<void> {
  const cfVariant = Cookies.get('X-AB-AI-Guide-Flow-13747')
  const localVariant = ab.experimentVariant(Experiment.ai_guide_flow_13747)

  if (cfVariant && !localVariant) {
    await ab.start(Experiment.ai_guide_flow_13747, cfVariant)
  }
}

export async function trialByPlan15535Experiment(): Promise<TrialByPlan15535Variant> {
  // We have experiment variant in the local storage.
  let result = ab.experimentVariant(
    Experiment.exp_trial_by_plan_15535,
  ) as TrialByPlan15535Variant

  const isAndroid = await platform.isAndroid()

  // We don't start the experiment on Android
  if (isAndroid) {
    return null
  }

  // Not Android, start the experiment
  if (result === null) {
    result = (await ab.start(
      Experiment.exp_trial_by_plan_15535,
    )) as TrialByPlan15535Variant
  }

  return result
}

export function trialByPlan15535Variant(): string | null {
  return ab.experimentVariant(
    Experiment.exp_trial_by_plan_15535,
  ) as TrialByPlan15535Variant
}

/**
 * The Fuzzy Search experiment.
 *
 * no - Use the current "literal" search, control variant.
 * yes - Use the fuzzy search, treatment variant
 */
export async function fuzzySearch15538Experiment(): Promise<FuzzySearch15538Variant> {
  let result = ab.experimentVariant(Experiment.exp_fuzzy_search_15538)

  if (result === null) {
    result = await ab.start(Experiment.exp_fuzzy_search_15538)
  }

  return result as FuzzySearch15538Variant
}

/**
 * Get the variant for the exp_fuzzy_search_15538 experiment.
 */
export function fuzzySearch15538Variant(): FuzzySearch15538Variant {
  return ab.experimentVariant(
    Experiment.exp_fuzzy_search_15538,
  ) as FuzzySearch15538Variant
}

/**
 * Get the variant for the ios_external_link_payment_16218 experiment.
 */
export async function iosExternalLinkPayment16218Experiment(): Promise<IosExternalLinkPayment16218Variant> {
  await platformData.init()
  const appBuild = platformData.appBuild
  // We need to get the storefront location for the experiment
  // and it's not available in builds < 60.
  if (appBuild < 60) {
    return null
  }
  const storefrontLocation = await getIosStorefrontLocation()
  // External payments are allowed in the US storefront only.
  if (storefrontLocation !== 'USA') {
    return null
  }

  // Check variant in the local storage
  let result = ab.experimentVariant(
    Experiment.exp_ios_external_link_payment_16218,
  )

  // Start the experiment
  if (result === null) {
    result = await ab.start(Experiment.exp_ios_external_link_payment_16218)
  }

  return result as IosExternalLinkPayment16218Variant
}

/**
 * Get the variant for the ios_external_link_payment_16218 experiment.
 */
export function iosExternalLinkPayment16218Variant(): IosExternalLinkPayment16218Variant {
  return ab.experimentVariant(
    Experiment.exp_ios_external_link_payment_16218,
  ) as IosExternalLinkPayment16218Variant
}

/**
 * Start the streaks_16464 experiment.
 *
 * A - Control, current logic without Streaks.
 * B - Version with the Streaks feature and generic push notifications.
 * C - Version with the Streaks feature and personalized push notifications.
 */
export async function streaks16464Experiment(): Promise<Streaks16464Variant> {
  let result = ab.experimentVariant(Experiment.exp_streaks_16464)

  if (result === null) {
    result = await ab.start(Experiment.exp_streaks_16464)
  }

  return result as Streaks16464Variant
}

/**
 * Get the variant for the streaks_16464 experiment.
 */
export function streaks16464Variant(): Streaks16464Variant {
  return ab.experimentVariant(
    Experiment.exp_streaks_16464,
  ) as Streaks16464Variant
}

/**
 * Shortcut to check whether to display streaks_16464 features.
 *
 * null, A: no
 * B, C: yes (the front-end display is the same for B and C)
 */
export function streaks16464ShowStreaks(): boolean {
  const variant = streaks16464Variant()
  return variant === 'B' || variant === 'C'
}

/**
 * Start the exp_onboarding_v7_flow_remove_reading_list_17546 experiment.
 *
 * A - Control, full onboarding flow.
 * B - Variant, trimmed onboarding flow.
 */
export async function onboardingRemoveReadingList17546(): Promise<OnboardingRemoveReadingList17546Variant> {
  let result = ab.experimentVariant(
    Experiment.exp_onboarding_v7_flow_remove_reading_list_17546,
  )

  if (result === null) {
    result = await ab.start(
      Experiment.exp_onboarding_v7_flow_remove_reading_list_17546,
    )
  }

  return result as OnboardingRemoveReadingList17546Variant
}

/**
 * Get the variant for the exp_onboarding_v7_flow_remove_reading_list_17546 experiment.
 */
export function onboardingRemoveReadingList17546Variant(): OnboardingRemoveReadingList17546Variant {
  return ab.experimentVariant(
    Experiment.exp_onboarding_v7_flow_remove_reading_list_17546,
  ) as OnboardingRemoveReadingList17546Variant
}

/**
 * Start the exp_integrated_checkout_experience_17351 experiment.
 *
 * A - Control, existing billing checkout experience.
 * B - Variant, integrated checkout experience.
 *
 * iOS/Android are excluded from this experiment.
 */
export async function integratedCheckoutExperience17351Experiment(): Promise<IntegratedCheckoutExperience17351Variant> {
  let result = integratedCheckoutExperience17351Variant()

  if (result === null) {
    result = (await ab.start(
      Experiment.exp_integrated_checkout_experience_17351,
    )) as IntegratedCheckoutExperience17351Variant
  }

  return result
}

/**
 * Get the variant for the exp_integrated_checkout_experience_17351 experiment.
 */
export function integratedCheckoutExperience17351Variant(): IntegratedCheckoutExperience17351Variant {
  return ab.experimentVariant(
    Experiment.exp_integrated_checkout_experience_17351,
  ) as IntegratedCheckoutExperience17351Variant
}

/**
 * Get the variant for the exp_persistent_partnership_offer_17477 experiment.
 */
export function persistentPartnershipOffer17477Variant(): PersistentPartnershipOffer17477Variant {
  return ab.experimentVariant(
    Experiment.exp_persistent_partnership_offer_17477,
  ) as PersistentPartnershipOffer17477Variant
}

/**
 * Start the persistent partnership offer experiment in the backend if we assigned a variant in the head.
 *
 * To avoid delaying render, eligible visitors get a variant in the inline script in the document head
 * (`variant_to_set_17477`); this runs after the app loads and sends it to the backend via `ab.start`.
 */
export async function savePersistentPartnershipOffer17477Variant(): Promise<void> {
  const newVariant = storage.getItem(VARIANT_TO_SET_17477_KEY)
  const localVariant = ab.experimentVariant(
    Experiment.exp_persistent_partnership_offer_17477,
  )

  if (newVariant && !localVariant) {
    await ab.start(
      Experiment.exp_persistent_partnership_offer_17477,
      newVariant,
    )
    storage.removeItem(VARIANT_TO_SET_17477_KEY)
  }
}

/**
 * Start the exp_null_experiment_17503 experiment on signup page visit.
 *
 * A - Control, no changes.
 * B - Variant, no changes.
 */
export async function signupPageNullExperiment17503Experiment(): Promise<NullExperiment17503Variant> {
  let result = ab.experimentVariant(Experiment.exp_null_experiment_17503)

  if (result === null) {
    result = await ab.start(Experiment.exp_null_experiment_17503)
  }

  return result as NullExperiment17503Variant
}

/*
 * Read experiment cookies set by CloudFront and transfer the experiment assignment to the local storage
 */
export async function homepageKitchenSinkRedesign17590(): Promise<void> {
  const cfVariant = Cookies.get('X-AB-HOMEPAGE-KITCHEN-SINK-REDESIGN-17590')
  const localVariant = ab.experimentVariant(
    Experiment.exp_homepage_kitchen_sink_redesign_v2_17590,
  )

  if (cfVariant && !localVariant) {
    await ab.start(
      Experiment.exp_homepage_kitchen_sink_redesign_v2_17590,
      cfVariant,
    )
  }
}
